import { create } from "@bufbuild/protobuf";

import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import {
  CreateSourceRequest,
  CreateSourceRequestSchema,
  CreateSourceResponse,
} from "@gx/ts-contracts/services/source/api/source_pb";
import {
  FileUploadIntentSchema,
  UploadMode,
} from "@gx/ts-contracts/services/source/resources/file_upload_intent/file_upload_intent_pb";
import {
  SourceFileUploadConfigurationSchema,
  SourceFileUploadFileSchema,
} from "@gx/ts-contracts/services/source/resources/source_configuration/file_upload/file_upload_pb";
import { SourceConfigurationSchema } from "@gx/ts-contracts/services/source/resources/source_configuration/source_configuration_pb";
import type { UploadSyncResource } from "@gx/ts-contracts/services/uploader/resources/uploader_pb";

import { SOURCE_FILE_UPLOAD_CONFIGURATION_CASE } from "@/pages/sources/components/create/sources/file-upload/constants";
import {
  CreateSourceFileUploadPhase,
  CreateSourceFileUploadState,
} from "@/pages/sources/components/create/sources/file-upload/types";
import type { FileUploadProgress } from "@/pages/sources/components/create/sources/types";
import { isCreateSourceNameProvided } from "@/pages/sources/components/create/sources/utils";

export interface FileUploadSourceProvisioningResult {
  sourceId: string;
  syncId: string;
  uploadSync: UploadSyncResource;
}

function buildSourceFileUploadFile(file: File) {
  return create(SourceFileUploadFileSchema, {
    intent: create(FileUploadIntentSchema, {
      sourcePath: file.name,
      uploadMode: UploadMode.SOURCE,
      fileSize: BigInt(file.size),
    }),
  });
}

export function buildCreateSourceFileUploadRequest(
  sourceName: string,
  files: File[],
): CreateSourceRequest {
  return create(CreateSourceRequestSchema, {
    name: sourceName.trim(),
    type: SourceType.FILE_UPLOAD,
    configuration: create(SourceConfigurationSchema, {
      configuration: {
        case: SOURCE_FILE_UPLOAD_CONFIGURATION_CASE,
        value: create(SourceFileUploadConfigurationSchema, {
          files: files.map(buildSourceFileUploadFile),
        }),
      },
    }),
  });
}

export function extractProvisioningResult(
  response: CreateSourceResponse,
): FileUploadSourceProvisioningResult {
  const sourceId = response.source?.id;
  if (!sourceId) {
    throw new Error("Create source response did not include a source id");
  }

  const sync = response.sync;
  if (!sync || !sync.id) {
    throw new Error("Create source response did not include a sync resource");
  }

  if (sync.syncData.case !== "uploadSync" || !sync.syncData.value) {
    throw new Error("Create source response did not include upload sync data");
  }

  const uploadSync = sync.syncData.value;
  if (!uploadSync.id) {
    throw new Error("Create source response did not include an upload sync");
  }

  if (uploadSync.operations.length === 0) {
    throw new Error("Create source response did not include upload operations");
  }

  return {
    sourceId,
    syncId: sync.id,
    uploadSync,
  };
}

export function filterDuplicateFiles(newFiles: File[], existingFiles: File[]): File[] {
  const existingNames = new Set(existingFiles.map((file) => file.name));
  return newFiles.filter((file) => !existingNames.has(file.name));
}

export function isFileUploadInProgress(phase: CreateSourceFileUploadPhase): boolean {
  return [
    CreateSourceFileUploadPhase.CREATING_SOURCE,
    CreateSourceFileUploadPhase.UPLOADING,
  ].includes(phase);
}

export function getFileProgressMap(
  fileProgressMap: Map<string, FileUploadProgress>,
): Map<string, FileUploadProgress> {
  const map = new Map<string, FileUploadProgress>();
  fileProgressMap.forEach((progress) => map.set(progress.file.name, progress));
  return map;
}

export function canSubmitUpload(state: CreateSourceFileUploadState): boolean {
  return !state.isUploading && isCreateSourceNameProvided(state) && state.files.length > 0;
}

export function isUploadDisabled(state: CreateSourceFileUploadState): boolean {
  return state.isUploading || state.phase === CreateSourceFileUploadPhase.COMPLETED;
}
